import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FaGithub, FaExternalLinkAlt, FaReact, FaVuejs, FaNodeJs, FaPython, FaMobile, FaServer, FaDatabase, FaAndroid, FaBrain, FaSearch } from 'react-icons/fa';
import OpenKuroShowcase from './OpenKuroShowcase';
import KaraOKShowcase from './KaraOKShowcase';
import SuiSuiBookShowcase from './SuiSuiBookShowcase';

const Projects = () => {
  const [sectionRef, inView] = useInView({
    triggerOnce: false,
    threshold: 0.1,
  });

  const [activeCategory, setActiveCategory] = useState('all');

  const sectionVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut',
        when: 'beforeChildren',
        staggerChildren: 0.2
      }
    }
  };

  const titleVariants = {
    hidden: { opacity: 0, y: -20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: 'easeOut' }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: 'easeOut' }
    },
    hover: {
      y: -10,
      boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.2), 0 10px 10px -5px rgba(0, 0, 0, 0.1)',
      transition: { duration: 0.3, ease: 'easeOut' }
    }
  };

  const buttonVariants = {
    inactive: { 
      scale: 1,
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
      color: '#ffffff'
    },
    active: { 
      scale: 1.05,
      backgroundColor: 'rgba(14, 165, 233, 0.2)',
      color: '#38bdf8',
      boxShadow: '0 0 15px rgba(56, 189, 248, 0.3)'
    },
    hover: { 
      scale: 1.05,
      backgroundColor: 'rgba(14, 165, 233, 0.1)'
    }
  };

  // 專案資料
  const projects = [
    {
      id: 0,
      title: 'OpenKuro',
      description: '隱私優先的個人 AI 助理，支援多模型切換、電腦控制、訊息整合與瀏覽器 GUI。實機展示請見下方 OpenKuro Showcase 區塊。',
      image: '/images/openkuro_cover.png',
      github: 'https://github.com/kaikaichumi/OpenKuro',
      tags: ['python', 'ai'],
      technologies: [
        { name: 'Python', icon: <FaPython className="text-blue-300" /> },
        { name: 'AI / LLM', icon: <FaBrain className="text-purple-400" /> }
      ]
    },
    {
      id: 0.5,
      title: 'RAG Code Search',
      description: '基於 RAG（檢索增強生成）技術的程式碼語意搜尋工具，透過向量資料庫建立程式碼索引，以自然語言查詢快速找到相關程式碼片段，提升開發效率。',
      image: '/images/000.jpg',
      github: 'https://github.com/kaikaichumi/RAG-Code-Search',
      tags: ['python', 'ai', 'rag'],
      technologies: [
        { name: 'Python', icon: <FaPython className="text-blue-300" /> },
        { name: 'RAG / LLM', icon: <FaBrain className="text-purple-400" /> }
      ]
    },
    {
      id: 0.6,
      title: 'KarOK Studio',
      description: '網頁版卡拉OK工具，讓使用者無需安裝即可在瀏覽器中享受卡拉OK體驗。上架僅兩天即突破 2,000 人次造訪。',
      image: '/images/000.jpg',
      link: 'https://karok.kaienet.work/',
      tags: ['react', 'webapp'],
      technologies: [
        { name: 'Web App', icon: <FaReact className="text-blue-400" /> },
      ]
    },
    {
      id: 0.7,
      title: 'SuiSui Book 美業預約系統',
      description: '多店家美業線上預約系統，支援美髮、美甲等服務。顧客可選擇服務、指定設計師與時段完成預約；店家後台可管理服務、人員與預約狀態，另有總管理端統一管理店家帳號。詳細介紹請見上方 SuiSui Book 展示區塊。',
      image: '/images/suisui_01.png',
      link: 'https://suisui.kaienet.com/',
      github: 'https://github.com/kaikaichumi/suisui-book',
      tags: ['vue', 'nodejs', 'webapp'],
      technologies: [
        { name: 'Vue 3', icon: <FaVuejs className="text-green-400" /> },
        { name: 'Node.js', icon: <FaNodeJs className="text-green-500" /> },
        { name: 'MongoDB', icon: <FaDatabase className="text-emerald-400" /> }
      ]
    },
    // 原始網站專案

    {
      id: 1,
      title: 'MakeNote MD筆記程式',
      description: 'MarkNote 是一款用Flutter開發跨平台的筆記應用程式，並使用 Markdown 格式。',
      image: '/images/makenote_01.png',
      link: 'https://makenote.kaienet.work/',
      github: 'https://github.com/kaikaichumi/MakeNote_web',
      tags: ['flutter', 'webapp'],
      technologies: [
        { name: 'Flutter', icon: <FaMobile className="text-blue-400" /> }
      ]
    },
    {
      id: 2,
      title: 'EZ購物平台',
      description: '使用vue和Node.js開發的電子商務平台，具有簡易操作的系統和完整的後台管理功能。',
      image: '/images/ezshop_02.png',
      github: 'https://github.com/kaikaichumi/ezShop',
      tags: ['vue', 'nodejs', 'webapp'],
      technologies: [
        { name: 'Vue', icon: <FaVuejs className="text-green-400" /> },
        { name: 'Node.js', icon: <FaNodeJs className="text-green-500" /> }
      ]
    },
    {
      id: 3,
      title: '經期追蹤APP',
      description: '使用Flutter開發的跨平台經期追蹤應用，整合數據分析，可以詳細紀錄更多身體狀況。',
      image: '/images/period_02.png',
      github: 'https://github.com/kaikaichumi/period-tracking',
      tags: ['flutter', 'android', 'mobile'],
      technologies: [
        { name: 'Flutter', icon: <FaMobile className="text-blue-400" /> },
        { name: 'Android', icon: <FaAndroid className="text-green-500" /> }
      ]
    },
    {
      id: 4,
      title: 'NFT門票發售與驗證系統',
      description: '利用區塊鏈技術開發的NFT門票系統，結合Flask/React技術實現。通過數位簽名和區塊鏈技術確保票券真實性，提高防偽能力並簡化票務管理流程。開發了具有獨特性與安全性的NFT門票系統，解決傳統電子票券問題',
      image: '/images/000.jpg',
      github: 'https://github.com/kaikaichumi/NFT_Ticket_System',
      tags: ['react', 'python', 'blockchain'],
      technologies: [
        { name: 'Flask', icon: <FaPython className="text-blue-300" /> },
        { name: 'React', icon: <FaReact className="text-blue-400" /> }
      ]
    },
    {
      id: 5,
      title: '大學專題 - 大樓管理系統',
      description: '成功整合網路通訊、資料庫和Android系統，開發一套完整的大樓管理系統。使用者可透過網頁介面管理住戶、大樓投票與公告等，後端使用Django/MySQL，搭配Android應用開發。',
      image: '/images/000.jpg',
      tags: ['django', 'mysql', 'android', 'webapp'],
      technologies: [
        { name: 'Django/MySQL', icon: <FaDatabase className="text-blue-500" /> },
        { name: 'Android', icon: <FaAndroid className="text-green-500" /> }
      ]
    },
    {
      id: 6,
      title: '輝光管數位時鐘',
      description: '使用Arduino微主控，設計可控制App應用與顯示時間、溫濕度、鬧鐘功能，採用Boost升壓電路將12V轉換成200V驅動輝光管。結合上世代產物與新世代電子控制的項目。',
      image: '/images/000.jpg',
      tags: ['arduino', 'mobile', 'hardware'],
      technologies: [
        { name: 'Arduino', icon: <FaServer className="text-red-400" /> },
        { name: 'Mobile App', icon: <FaMobile className="text-blue-400" /> }
      ]
    },
    {
      id: 7,
      title: '超市人流計數系統',
      description: '透過openCV採用CNN模型檢測出人物，透過監視系統即可計算當日進店人數。',
      image: '/images/000.jpg',
      tags: ['python', 'ai', 'deeplearning'],
      technologies: [
        { name: 'Python', icon: <FaPython className="text-blue-300" /> },
        { name: 'Deep Learning', icon: <FaBrain className="text-purple-500" /> }
      ]
    },
    {
      id: 8,
      title: 'VQGAN-CLIP 文本對影像生成並發佈NFT',
      description: '研究AI影像生成與區塊鏈NFT結合的應用，使用生成對抗網路(GAN)產生特殊圖像作為NFT。。',
      image: '/images/000.jpg',
      tags: ['react', 'python', 'ai', 'blockchain'],
      technologies: [
        { name: 'Flask/React', icon: <FaReact className="text-blue-400" /> },
        { name: 'Python', icon: <FaPython className="text-blue-300" /> }
      ]
    },
    {
      id: 9,
      title: '機器學習專案 - 虛擬貨幣預測分析',
      description: '利用機器學習模型分析虛擬貨幣價格趨勢，實作lstm模型和Decision tree，並成功達到70%及60%的準確率。透過數據處理與特徵工程提升預測精準度。',
      image: '/images/000.jpg',
      tags: ['python', 'ai', 'machinelearning'],
      technologies: [
        { name: 'Python', icon: <FaPython className="text-blue-300" /> },
        { name: 'Machine Learning', icon: <FaBrain className="text-purple-400" /> }
      ]
    }
  ];

  // 篩選類別
  const categories = [
    { id: 'all', name: '全部' },
    { id: 'react', name: 'React' },
    { id: 'python', name: 'Python' },
    { id: 'vue', name: 'Vue.js' },
    { id: 'nodejs', name: 'Node.js' },
    { id: 'flutter', name: 'Flutter' },
    { id: 'django', name: 'Django' },
    { id: 'mysql', name: 'MySQL' },
    { id: 'android', name: 'Android' },
    { id: 'ai', name: '人工智慧' },
    { id: 'rag', name: 'RAG / LLM' },
    { id: 'deeplearning', name: '深度學習' },
    { id: 'machinelearning', name: '機器學習' },
    { id: 'blockchain', name: '區塊鏈/NFT' },
    { id: 'arduino', name: 'Arduino' },
    { id: 'hardware', name: '硬體開發' },
    { id: 'webapp', name: '網頁應用' },
    { id: 'mobile', name: '行動應用' }
  ];

  // 篩選專案
  const filteredProjects = activeCategory === 'all' 
    ? projects 
    : projects.filter(project => project.tags.includes(activeCategory));

  const handleCategoryClick = (categoryId) => {
    setActiveCategory(categoryId);
  };

  return (
    <section id="projects" className="py-20 relative">
      {/* 背景裝飾 */}
      <div className="absolute inset-0 z-0">
        <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-slate-900/50 via-slate-800/50 to-slate-900/50"></div>
        <div className="absolute top-40 right-20 w-72 h-72 rounded-full bg-primary-900/10 blur-3xl"></div>
        <div className="absolute bottom-40 left-20 w-80 h-80 rounded-full bg-secondary-900/10 blur-3xl"></div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          ref={sectionRef}
          variants={sectionVariants}
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
        >
          <motion.div className="text-center mb-16" variants={titleVariants}>
            <motion.h2 
              variants={titleVariants} 
              className="text-3xl md:text-4xl font-bold mb-4 inline-block"
            >
              我的<span className="text-gradient">作品集</span>
            </motion.h2>
            <motion.div variants={titleVariants} className="w-20 h-1 bg-gradient-to-r from-primary-500 to-secondary-500 mx-auto mb-8"></motion.div>
            <motion.p variants={titleVariants} className="text-lg text-gray-300 max-w-2xl mx-auto">
              探索我的專案作品集，包含網頁應用、行動應用以及AI應用等多元領域的開發成果。
              每個專案都展現了我對技術的熱情和解決問題的能力。
            </motion.p>
          </motion.div>

          {/* OpenKuro 精選展示 */}
          <OpenKuroShowcase />

          {/* KaraOK 精選展示 */}
          <KaraOKShowcase />

          {/* SuiSui Book 精選展示 */}
          <SuiSuiBookShowcase />

          {/* 分類篩選 */}
          <motion.div
            className="flex flex-wrap justify-center gap-2 mb-8 sm:mb-12 px-4"
            variants={titleVariants}
          >
            {categories.map(category => (
              <motion.button
                key={category.id}
                onClick={() => handleCategoryClick(category.id)}
                className="px-3 py-1.5 sm:px-4 sm:py-2 rounded-full text-xs sm:text-sm font-medium transition-all duration-300 whitespace-nowrap"
                variants={buttonVariants}
                initial="inactive"
                animate={activeCategory === category.id ? 'active' : 'inactive'}
                whileHover={activeCategory !== category.id ? 'hover' : ''}
              >
                {category.name}
              </motion.button>
            ))}
          </motion.div>

          {/* 專案網格 */}
          <motion.div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
            variants={sectionVariants}
            initial="hidden"
            animate={inView ? 'visible' : 'hidden'}
          >
            {filteredProjects.length > 0 ? (
              filteredProjects.map(project => (
                <ProjectCard key={project.id} project={project} />
              ))
            ) : (
              <motion.div 
                className="col-span-3 text-center py-12"
                variants={cardVariants}
              >
                <p className="text-gray-400 text-lg">暫無符合此類別的專案</p>
              </motion.div>
            )}
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

// 專案卡片元件
const ProjectCard = ({ project }) => {
  const [cardRef, inView] = useInView({
    triggerOnce: false,
    threshold: 0.1,
  });

  const [isHovered, setIsHovered] = useState(false);

  return (
    <motion.div
      ref={cardRef}
      className="card overflow-hidden"
      variants={{
        hidden: { opacity: 0, y: 30 },
        visible: {
          opacity: 1,
          y: 0,
          transition: { duration: 0.5, ease: 'easeOut' }
        }
      }}
      whileHover={{ y: -10 }}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
    >
      <div className="relative overflow-hidden mb-4 rounded-lg aspect-video">
        <motion.img
          src={project.image}
          alt={project.title}
          className="w-full h-full object-cover"
          animate={{ scale: isHovered ? 1.05 : 1 }}
          transition={{ duration: 0.4 }}
        />
        <motion.div 
          className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/50 to-transparent flex items-end p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: isHovered ? 1 : 0 }}
          transition={{ duration: 0.3 }}
        >
          <div className="flex gap-2">
            <motion.a
              href={project.github}
              target="_blank"
              rel="noopener noreferrer"
              className="w-10 h-10 rounded-full bg-slate-800/80 flex items-center justify-center text-white hover:bg-primary-600 transition-colors"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <FaGithub />
            </motion.a>
            {project.link && (
              <motion.a
                href={project.link}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-slate-800/80 flex items-center justify-center text-white hover:bg-primary-600 transition-colors"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <FaExternalLinkAlt />
              </motion.a>
            )}
          </div>
        </motion.div>
      </div>

      <div>
        <h3 className="text-xl font-bold mb-2">{project.title}</h3>
        <p className="text-gray-300 text-sm mb-4">{project.description}</p>
        
        <div className="flex flex-wrap gap-2">
          {project.technologies.map((tech, index) => (
            <div 
              key={index} 
              className="flex items-center gap-1 px-3 py-1 rounded-full bg-slate-800/50 text-xs font-medium"
            >
              {tech.icon}
              <span>{tech.name}</span>
            </div>
          ))}
        </div>
      </div>
    </motion.div>
  );
};

export default Projects;
